import { FC, useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Code as CodeIcon,
  PushPin as PinFilledIcon,
  PushPinOutlined as PinIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { IconTypes } from "../icons.types";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import { EditorField } from "../../features/data/editor";
import { validateHtml } from "./editor-func";
import { trackEvent } from "../../features/analytics/analytics";
import { getSnippetText } from "../../features/design/expression-snippets";

const SCRIPT_TAG_RE = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script\s*>/gi;

function sanitizeForPreview(html: string): string {
  return html.replace(SCRIPT_TAG_RE, "");
}

export interface RawHtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSwitchToWysiwyg: () => void;
  onExternalEdit?: () => void;
  disableExternalEdit?: boolean;
  /** Pin current mode as global default for all editors */
  onPinMode?: () => void;
  /** Whether current mode matches the global default */
  isDefaultMode?: boolean;
  fields?: EditorField[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArgs?: any;
  compact?: boolean;
  maxHeight?: number;
  fullScreen?: boolean;
}

export const RawHtmlEditor: FC<RawHtmlEditorProps> = ({
  value,
  onChange,
  onSwitchToWysiwyg,
  onExternalEdit,
  disableExternalEdit,
  onPinMode,
  isDefaultMode,
  fields,
  fieldArgs,
  compact,
  maxHeight,
  fullScreen,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [fieldsAnchor, setFieldsAnchor] = useState<null | HTMLElement>(null);
  const [htmlWarning, setHtmlWarning] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resolveContent = fieldArgs?.resolveContent as
    | ((html: string) => string)
    | undefined;

  const previewHtml = useMemo(() => {
    if (!value) return "";
    if (resolveContent) {
      try {
        return resolveContent(value);
      } catch {
        return sanitizeForPreview(value);
      }
    }
    return sanitizeForPreview(value);
  }, [value, resolveContent]);

  const insertAtCursor = useCallback(
    (text: string) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        onChange((value ?? "") + text);
        return;
      }
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue =
        (value ?? "").substring(0, start) + text + (value ?? "").substring(end);
      onChange(newValue);
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + text.length;
        textarea.focus();
      });
    },
    [value, onChange],
  );

  const insertField = useCallback(
    (field: EditorField) => {
      trackEvent({
        category: "designer",
        action: "insert-expression",
        label: field.content,
        metadata: { source: "html-editor" },
        internal: true,
      });
      insertAtCursor(getSnippetText(field.content, fieldArgs?.product?.category));
    },
    [insertAtCursor, fieldArgs],
  );

  const editorHeight = (() => {
    if (maxHeight) return `${maxHeight}px`;
    if (fullScreen) return "calc(100vh - 250px)";
    if (compact) return "120px";
    return "auto";
  })();

  const editorMinHeight = compact ? "50px" : "300px";

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: compact ? 0.5 : 1,
          minHeight: compact ? 36 : 48,
          px: 0,
        }}
      >
        {/* Mode toggle — segmented button group */}
        <ButtonGroup size="small" variant="outlined">
          <Tooltip title="Switch to Visual mode (may reformat HTML)">
            <Button
              onClick={onSwitchToWysiwyg}
              startIcon={compact ? undefined : <FormatColorTextIcon />}
              sx={{
                textTransform: "none",
                minWidth: compact ? 0 : undefined,
                px: compact ? 1 : undefined,
              }}
            >
              {compact ? (
                <FormatColorTextIcon sx={{ fontSize: 18 }} />
              ) : (
                "Visual"
              )}
            </Button>
          </Tooltip>
          <Tooltip title="Preview HTML output">
            <Button
              variant={showPreview ? "contained" : "outlined"}
              onClick={() => setShowPreview(!showPreview)}
              startIcon={
                compact ? undefined : showPreview ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )
              }
              sx={{
                textTransform: "none",
                minWidth: compact ? 0 : undefined,
                px: compact ? 1 : undefined,
              }}
            >
              {compact ? (
                showPreview ? (
                  <VisibilityOff sx={{ fontSize: 18 }} />
                ) : (
                  <Visibility sx={{ fontSize: 18 }} />
                )
              ) : (
                "Preview"
              )}
            </Button>
          </Tooltip>
        </ButtonGroup>

        {/* Expression fields */}
        {fields && fields.length > 0 && (
          <>
            <Tooltip title="Insert expression">
              <IconButton
                size="small"
                onClick={(e) => setFieldsAnchor(e.currentTarget)}
              >
                <IconTypes.Fields />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={fieldsAnchor}
              open={Boolean(fieldsAnchor)}
              onClose={() => setFieldsAnchor(null)}
            >
              {fields.map((f) =>
                f.name ? (
                  <MenuItem
                    key={f.key}
                    onClick={() => {
                      insertField(f);
                      setFieldsAnchor(null);
                    }}
                  >
                    <ListItemText
                      primary={f.name}
                      secondary={f.desc}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </MenuItem>
                ) : (
                  <Divider key={f.key} />
                ),
              )}
            </Menu>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Edit in popup */}
        {!disableExternalEdit &&
          onExternalEdit &&
          (compact ? (
            <Tooltip title="Edit in Popup">
              <IconButton size="small" onClick={onExternalEdit}>
                <IconTypes.OpenInNew sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<IconTypes.OpenInNew />}
              onClick={onExternalEdit}
              sx={{
                textTransform: "none",
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "text.primary",
                  color: "text.primary",
                },
              }}
            >
              Edit in Popup
            </Button>
          ))}

        {/* Mode indicator + pin */}
        <Tooltip
          title={
            isDefaultMode
              ? "HTML is your default mode (click to switch default to Visual)"
              : "Set HTML as default for all editors"
          }
        >
          <Box
            onClick={onPinMode}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              border: "1px solid",
              borderColor: isDefaultMode ? "primary.main" : "divider",
              bgcolor: isDefaultMode
                ? "rgba(25, 118, 210, 0.08)"
                : "action.selected",
              color: isDefaultMode ? "primary.main" : "text.secondary",
              cursor: onPinMode ? "pointer" : "default",
              transition: "all 0.15s",
              "&:hover": onPinMode
                ? { borderColor: "primary.main", color: "primary.main" }
                : {},
            }}
          >
            <CodeIcon sx={{ fontSize: 14 }} />
            {!compact && (
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ fontSize: "0.7rem", lineHeight: 1 }}
              >
                HTML
              </Typography>
            )}
            {isDefaultMode ? (
              <PinFilledIcon sx={{ fontSize: 13 }} />
            ) : (
              <PinIcon sx={{ fontSize: 13 }} />
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* Editor area */}
      {htmlWarning && (
        <Alert
          severity="warning"
          onClose={() => setHtmlWarning(null)}
          sx={{ mb: 0.5, py: 0, fontSize: "0.8rem" }}
        >
          {htmlWarning}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Raw HTML Textarea */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="textarea"
            ref={textareaRef}
            value={value ?? ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onChange(e.target.value)
            }
            onBlur={() => setHtmlWarning(validateHtml(value))}
            spellCheck={false}
            sx={{
              width: "100%",
              height: editorHeight,
              minHeight: editorMinHeight,
              fontFamily: "'Cascadia Code', 'Fira Code', 'Consolas', monospace",
              fontSize: "0.85rem",
              lineHeight: 1.5,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "#1e1e1e",
              color: "#d4d4d4",
              resize: "vertical",
              outline: "none",
              boxSizing: "border-box",
              "&:focus": {
                borderColor: "primary.main",
              },
            }}
          />
        </Box>

        {/* Preview panel */}
        {showPreview && (
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              p: 2,
              height: editorHeight,
              minHeight: editorMinHeight,
              overflow: "auto",
              minWidth: 0,
              "& img": { maxWidth: "100%" },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1,
                color: "text.secondary",
                borderBottom: "1px solid",
                borderColor: "divider",
                pb: 0.5,
              }}
            >
              {resolveContent ? "Preview (with product data)" : "Preview"}
            </Typography>
            {value ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: previewHtml,
                }}
              />
            ) : (
              <Typography color="text.secondary" fontStyle="italic">
                No content to preview
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};
